import React, { useState } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { CompanyUserForm } from "../companyUsers/companyUserForm.component";
import {
  StampClockCreateActions,
  StampClockCreateDescription,
  StampClockCreatedEmail,
} from "./companyStampClock.styled";

type DialogState =
  | { readonly type: "closed" }
  | { readonly type: "create" }
  | { readonly email: string; readonly type: "created" };

function CreatedContent(props: { readonly email: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <StampClockCreateDescription>
        {t("CompanyStampClock.CreatedDescription")}
      </StampClockCreateDescription>
      <StampClockCreatedEmail>
        <strong>{t("CompanyStampClock.EmailLabel")}:</strong> {props.email}
      </StampClockCreatedEmail>
    </>
  );
}

export const CreateStampClock: React.FC = () => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<DialogState>({ type: "closed" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCreated = dialog.type === "created";

  const closeDialog = (): void => {
    if (!isSubmitting) {
      setDialog({ type: "closed" });
    }
  };

  return (
    <>
      <StampClockCreateActions>
        <PrimaryButton
          label={t("CompanyStampClock.CreateButton")}
          iconName="v2/plus"
          onClick={() => setDialog({ type: "create" })}
        />
      </StampClockCreateActions>
      <Modal
        label={t(
          isCreated
            ? "CompanyStampClock.CreatedTitle"
            : "CompanyStampClock.CreateTitle",
        )}
        isOpen={dialog.type !== "closed"}
        onClose={closeDialog}
        size="compact"
        showCloseButton={isCreated}
        footer={
          isCreated ? (
            <PrimaryButton
              label={t("CompanyStampClock.OpenSimflexity")}
              onClick={() =>
                window.open(
                  window.location.origin,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            />
          ) : undefined
        }
      >
        {dialog.type === "created" ? (
          <CreatedContent email={dialog.email} />
        ) : (
          <>
            <StampClockCreateDescription>
              {t("CompanyStampClock.CreateDescription")}
            </StampClockCreateDescription>
            <CompanyUserForm
              mode="stampClock"
              onCancel={closeDialog}
              onCreated={(email) => setDialog({ email, type: "created" })}
              onSubmittingChange={setIsSubmitting}
            />
          </>
        )}
      </Modal>
    </>
  );
};
